import { useCallback, useState } from 'react'
import { defaultEditorTheme } from '../../templateEditor.theme'
import { InlineEditTarget, TemplateElement } from '../../templateEditor.types'
import { removeListItem } from '../../elements/tableListOps'
import { TemplateEditorHistoryApi } from './useTemplateEditorHistory'

type Params = {
    setElements: React.Dispatch<React.SetStateAction<TemplateElement[]>>
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>
    history: TemplateEditorHistoryApi
}

const applyInlineText = (element: TemplateElement, target: InlineEditTarget, text: string): TemplateElement => {
    if (target.type === 'text' && element.type === 'text' && element.id === target.elementId) {
        return { ...element, text }
    }

    if (target.type === 'list-item' && element.type === 'list' && element.id === target.elementId) {
        const trimmed = text.trim()
        // Пустой пункт = удаление строки (минимум один пункт оставляем)
        if (!trimmed) {
            if (element.items.length <= 1) {
                return { ...element, items: [''] }
            }
            return removeListItem(element, target.index, defaultEditorTheme)
        }

        const items = [...element.items]
        items[target.index] = text
        return { ...element, items }
    }

    if (target.type === 'table-cell' && element.type === 'table' && element.id === target.elementId) {
        const data = element.data.map((row, rowIndex) =>
            rowIndex === target.row ? row.map((cell, colIndex) => (colIndex === target.col ? text : cell)) : row
        )
        return { ...element, data }
    }

    return element
}

export const useTemplateEditorInlineEdit = ({ setElements, setSelectedIds, history }: Params) => {
    const [inlineEdit, setInlineEdit] = useState<InlineEditTarget | null>(null)

    const startInlineEdit = useCallback(
        (target: InlineEditTarget) => {
            setSelectedIds([target.elementId])
            setInlineEdit(target)
        },
        [setSelectedIds]
    )

    const cancelInlineEdit = useCallback(() => {
        setInlineEdit(null)
    }, [])

    const commitInlineEdit = useCallback(
        (text: string) => {
            if (!inlineEdit) {
                return
            }

            const target = inlineEdit
            setInlineEdit(null)
            history.pushHistory()
            setElements((prev) => prev.map((element) => applyInlineText(element, target, text)))
        },
        [history, inlineEdit, setElements]
    )

    const isEditing = useCallback(
        (target: InlineEditTarget) => {
            if (!inlineEdit) {
                return false
            }

            if (inlineEdit.type !== target.type || inlineEdit.elementId !== target.elementId) {
                return false
            }

            if (target.type === 'list-item' && inlineEdit.type === 'list-item') {
                return inlineEdit.index === target.index
            }

            if (target.type === 'table-cell' && inlineEdit.type === 'table-cell') {
                return inlineEdit.row === target.row && inlineEdit.col === target.col
            }

            return true
        },
        [inlineEdit]
    )

    return {
        inlineEdit,
        startInlineEdit,
        cancelInlineEdit,
        commitInlineEdit,
        isEditing,
    }
}

export type TemplateEditorInlineEditApi = ReturnType<typeof useTemplateEditorInlineEdit>
